import { FC } from 'react';
import { MenuItem, TextField, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { UsageRangePreset } from '@libs/constants';
import { useTrans } from '@libs/i18n';

import { adminColors } from '../adminTheme';

interface Props {
  value: string;
  onChange: (preset: string) => void;
}

/**
 * Named windows only — no custom date range.
 *
 * `apps/web` has no date-picker dependency (`@mui/x-date-pickers` is not in its
 * package.json), and presets sidestep the cross-field validation, the
 * range-too-large path and the "which timezone is this date in?" question
 * entirely. The reporting timezone is fixed platform-wide and shown next to
 * this control, so a window is never ambiguous.
 */
export const UsageRangePicker: FC<Props> = observer(({ value, onChange }) => {
  const { t } = useTrans();
  const theme = useTheme();
  const c = adminColors(theme);

  const options: Array<{ value: string; label: string }> = [
    { value: UsageRangePreset.TODAY, label: t('app.admin.usage.range.today') },
    { value: UsageRangePreset.LAST_7D, label: t('app.admin.usage.range.7d') },
    { value: UsageRangePreset.LAST_30D, label: t('app.admin.usage.range.30d') },
    { value: UsageRangePreset.MONTH_TO_DATE, label: t('app.admin.usage.range.mtd') },
    { value: UsageRangePreset.PREVIOUS_MONTH, label: t('app.admin.usage.range.prevMonth') },
  ];

  return (
    <TextField
      select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        minWidth: 160,
        '& .MuiInputBase-root': { fontSize: 13, borderRadius: 2, backgroundColor: c.surface },
      }}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
});
